import { get } from './shared';
import authService from './auth.service';
import { editingMissionsUpdated } from '../actions/editing-missions-updated.action';
import { editingQuestUpdated } from '../actions/editing-quest-updated.action';
import { questEditorErrorsFound } from '../actions/quest-editor-errors-found.action';
import { navigateComponent } from '../actions/app.action.js';
import { BaseService } from './base.service';

export class QuestEditorService extends BaseService{
  constructor() {
    super();

    this.validators = [
      {
        name: 'months',
        min: 1,
        max: 12
      },
      {
        name: 'sideQuest',
        invalid: 0
      }
    ];
  }

  async getEditingQuest(questId) {
    const upn = authService.getUserPrincipleName();

    const response = await get(this.buildApiUrl(`quest?questId=${questId}&upn=${upn}`));
    const data = await response.json();
    const quest = data.quest;
    let missions = data.missions;

    if (quest) {
      quest.startDate = new Date(quest.startDate);
      quest.endDate = new Date(quest.endDate);
      missions = missions.map(mission => {
        const dateCompleted = mission.dateCompleted ? new Date(mission.dateCompleted) : mission.dateCompleted;
        return {
          ...mission,
          dateCompleted
        };
      });

      this.store.dispatch(editingQuestUpdated(quest));
      this.store.dispatch(editingMissionsUpdated(missions));
    } else {
      this.store.dispatch(navigateComponent('/permission'));
    }
  }

  updateEditingQuest(quest) {
    if (!quest.months)  {
      quest.endDate = undefined
    } else {
      // quest.months is provided and thus endDate can be calculated
    }

    this.store.dispatch(editingQuestUpdated(quest));
  }

  updateEditingMissions(missions) {
    this.store.dispatch(editingMissionsUpdated(missions));
  }

  updateQuestErrors(errors) {
    this.store.dispatch(questEditorErrorsFound(errors));
  }

  updateMissionsSortOrder(allMissions, missionToUpdate, newSortOrder) {
    const oldSortOrder = missionToUpdate.sortOrder;
    const otherMission = allMissions.find((m) => m.sortOrder === newSortOrder);

    const changedMissions = allMissions.map((m) => {
      let sortOrder = m.sortOrder;
      if (m.missionId === missionToUpdate.missionId) {
        sortOrder = newSortOrder;
      } else if (m.missionId === otherMission.missionId) {
        sortOrder = oldSortOrder;
      }

      return {
        ...m,
        sortOrder
      };
    });

    const missions = changedMissions.sort(this.compareMissions);
    this.store.dispatch(editingMissionsUpdated(missions));
  }

  saveMissionSortOrder(missions) {
    const newMissions = missions.sort(this.compareMissions);
    this.store.dispatch(editingMissionsUpdated(newMissions));
  }

  markMissionForDeletion(allMissions, missionToDelete) {
    const deletedSortOrder = missionToDelete.sortOrder;
    let missions = allMissions.map((m) => {
      if (m.missionId === missionToDelete.missionId) {
        return {
          ...missionToDelete,
          sortOrder: -1,
        };
      }
      let sortOrder = m.sortOrder;
      if (m.sortOrder > deletedSortOrder) {
        sortOrder = sortOrder - 1;
      }

      return { ...m, sortOrder };
    });
    missions = missions.sort(this.compareMissions);
    this.store.dispatch(editingMissionsUpdated(missions));
    return missions;
  }

  compareMissions(missionA, missionB) {
    return missionA.sortOrder - missionB.sortOrder;
  }

  createNewMission(allMissions, newMission) {
    let sortOrder = 1;
    let missionId = -1;
    if (allMissions.length) {
      sortOrder = this.getNewSortOrder(allMissions);
      missionId = this.getNewMissionId(allMissions);
    }

    const missions = [...allMissions, { ...newMission, sortOrder, missionId }];
    this.store.dispatch(editingMissionsUpdated(missions));
  }

  validCourseMission(allMissions, newMission) {
    return !allMissions.filter((c) => c.courseId === newMission.courseId && !c.deleted)
      .length;
  }

  getNewSortOrder(missions) {
    const sortOrders = missions.map((m) => m.sortOrder);
    return Math.max(...sortOrders) + 1;
  }

  getNewMissionId(missions) {
    let missionId = -1;
    const missionIds = missions.map((m) => m.missionId);
    const minMissionId = Math.min(...missionIds);
    if (minMissionId < 0) {
      missionId = minMissionId - 1;
    }

    return missionId;
  }

  updateMission(allMissions, updatedMission) {
    const missions = allMissions.map((m) => {
      if (m.missionId === updatedMission.missionId) {
        return updatedMission;
      }

      return m;
    });

    this.store.dispatch(editingMissionsUpdated(missions));
  }

  validateField(changedField, errorList) {
    const validator = this.validators.find((v) => v.name === changedField.name);
    this.removeError(errorList, changedField.name);

    if (!changedField.value) {
      errorList.push(changedField.name);
    } else if (validator) {
      if (
        (validator.min && validator.min > changedField.value) ||
        (validator.max && validator.max < changedField.value)
      ) {
        errorList.push(changedField.name);
      } else if (changedField.value == validator.invalid) {
        errorList.push(changedField.name);
      } else {
        this.removeError(errorList, changedField.name);
      }
    } else {
      this.removeError(errorList, changedField.name);
    }
  };

  removeError(errorList, changedFieldName) {
    const indexOfError = errorList.indexOf(changedFieldName);
    if (indexOfError >= 0) {
      errorList.splice(indexOfError, 1);
    }
  };
}

export default new QuestEditorService();
